import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useSupabaseQuery } from "@/hooks/useSupabaseQuery";
import {
  fetchOwnSubscription,
  fetchRecentSignals,
  fetchSystemHealth,
  fetchTopStrategies,
} from "@/features/dashboard/api";
import { DashboardStatusBar } from "@/features/dashboard/DashboardStatusBar";
import { ActiveSignalsCard } from "@/features/dashboard/ActiveSignalsCard";
import { WinRateCard } from "@/features/dashboard/WinRateCard";
import { TodaysSignalsCard } from "@/features/dashboard/TodaysSignalsCard";
import { StrategyMiniCard } from "@/features/dashboard/StrategyMiniCard";
import { AccountSummaryCard } from "@/features/dashboard/AccountSummaryCard";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { QueryState } from "@/components/feedback/QueryState";
import { SkeletonTable } from "@/components/feedback/SkeletonTable";
import { formatPercentage, formatRelativeTime } from "@/lib/format";

const PREMIUM_PLAN_NAMES = new Set(["Elite"]);

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id ?? "";

  const signalsQuery = useSupabaseQuery(() => fetchRecentSignals(8), []);
  const subscriptionQuery = useSupabaseQuery(() => fetchOwnSubscription(userId), [userId]);
  const healthQuery = useSupabaseQuery(() => fetchSystemHealth(), []);
  const topStrategiesQuery = useSupabaseQuery(() => fetchTopStrategies(3), []);

  const planName = subscriptionQuery.data?.plans?.name ?? "Free";
  const isPremiumPlan = PREMIUM_PLAN_NAMES.has(planName);

  return (
    <div className="page page-dashboard">
      <DashboardStatusBar
        planName={planName}
        isPremiumPlan={isPremiumPlan}
        workers={healthQuery.data ?? []}
      />

      <div className="dashboard-stat-grid">
        <ActiveSignalsCard />
        <WinRateCard />
        <TodaysSignalsCard />
      </div>

      <Card title="Recent signals" className="dashboard-card-clickable" onClick={() => navigate("/signals")}>
        <QueryState
          isLoading={signalsQuery.isLoading}
          error={signalsQuery.error}
          loadingFallback={<SkeletonTable rows={5} columns={4} />}
          isEmpty={(signalsQuery.data ?? []).length === 0}
          emptyTitle="No signals yet"
          emptyMessage="Scanning markets — your first signal will appear here once one is generated."
        >
          <table className="data-table dashboard-recent-signals-table">
            <thead>
              <tr>
                <th>Direction</th>
                <th>Timeframe</th>
                <th>Confidence</th>
                <th>Status</th>
                <th>Generated</th>
              </tr>
            </thead>
            <tbody>
              {(signalsQuery.data ?? []).map((signal) => (
                <tr key={signal.id}>
                  <td>
                    <Badge tone={signal.direction === "buy" ? "positive" : "negative"}>
                      {signal.direction.toUpperCase()}
                    </Badge>
                  </td>
                  <td>{signal.timeframe}</td>
                  <td className="font-numeric">{formatPercentage(signal.confidence)}</td>
                  <td>
                    <Badge tone={signal.status === "active" ? "info" : "neutral"}>
                      {signal.status}
                    </Badge>
                  </td>
                  <td className="font-numeric">{formatRelativeTime(signal.generated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>

      <div className="dashboard-secondary-grid">
        <Card title="Strategy performance" className="dashboard-card-clickable" onClick={() => navigate("/strategies")}>
          <QueryState
            isLoading={topStrategiesQuery.isLoading}
            error={topStrategiesQuery.error}
            isEmpty={(topStrategiesQuery.data ?? []).length === 0}
            emptyMessage="No production strategies yet."
          >
            <div>
              {(topStrategiesQuery.data ?? []).map((strategy) => (
                <StrategyMiniCard key={strategy.strategy_version_id} strategy={strategy} />
              ))}
            </div>
          </QueryState>
        </Card>

        <Card title="Account" className="dashboard-card-clickable" onClick={() => navigate("/account")}>
          <QueryState isLoading={subscriptionQuery.isLoading} error={subscriptionQuery.error}>
            <AccountSummaryCard subscription={subscriptionQuery.data} />
          </QueryState>
        </Card>
      </div>
    </div>
  );
}
